import { ArrowRight, Sparkles } from 'lucide-react';
import type { CustomerDeskView } from '../api';
import type { JsonDTO } from '../../../src/domain/json';
import type { readCase } from '../../../src/application/collections';
import { caseStatusLabels } from '../../../src/domain/collections/cases';
import { caseStory, type StoryStep } from '../../../src/domain/collections/story';
import { formatDay, useDeskData } from './desk';
import type { Brief } from './customer-360';
import '../pages/collections.css';

type Found = JsonDTO<NonNullable<Awaited<ReturnType<typeof readCase>>>>;

function stamp(step: StoryStep, persona: string, asOf: string) {
  const who = step.who === 'customer' ? 'Customer' : step.who === 'agent' ? persona : step.who === 'person' ? 'Your team' : null;
  const when = step.at ? (step.at.slice(0, 10) === asOf.slice(0, 10) ? 'Today' : formatDay(step.at, asOf)) : null;
  return [who, when].filter(Boolean).join(' · ');
}

/**
 * The top of the customer page: the same status and situation line as Collections, then what they said, what we did and
 * what happens next. The next step is said here once; the card below carries its detail.
 */
export function CaseStory({ desk, brief, briefError }: { desk: CustomerDeskView; brief: Brief | null; briefError: string }) {
  const { data } = useDeskData<Found>(`/app-api/collections/${encodeURIComponent(desk.customer.id)}`);
  const persona = desk.persona;
  const found = data?.case;
  const story = found ? caseStory(found, desk.timeline, persona, desk.nextStep.summary) : null;
  const waiting = found && found.itemKeys.length > 0;
  return <section className="case-story" aria-labelledby="case-story-line" aria-busy={!story}>
    <header className="case-story-head">
      {found && <span className={`col-pill s-${found.status}`}>{caseStatusLabels[found.status]}</span>}
      <h2 id="case-story-line">{story?.headline ?? 'Reading this account…'}</h2>
      {waiting && <a className="case-story-cta" href={`#collections/${encodeURIComponent(found.id)}`}>
        {found.itemKeys.length === 1 ? 'Decide in Collections' : `Decide ${found.itemKeys.length} in Collections`}<ArrowRight size={14} aria-hidden /></a>}
    </header>
    {story && <ol className="case-story-steps">{story.steps.map(s => {
      const meta = s.kind === 'next' && s.note ? null : stamp(s, persona, desk.asOf);
      return <li key={s.kind} className={`case-story-step ${s.kind}${s.at || s.kind === 'next' ? '' : ' empty'}`}>
        <span className="case-story-label">{s.label}</span>
        <p>{s.href ? <a href={s.href}>{s.text}</a> : s.text}</p>
        {s.note && <small className="case-story-note">{s.note}</small>}
        {meta && <small className="case-story-meta">{meta}</small>}
      </li>;
    })}</ol>}
    <div className="case-story-read" aria-live="polite">
      <Sparkles size={14} aria-hidden />
      <div>
        <strong>{persona}’s read</strong>
        {/* Both the model prompt and the rules brief end on the plan, which What happens next already says. */}
        {brief ? <p>{(brief.lines.length > 1 ? brief.lines.slice(0, -1) : brief.lines).join(' ')}
          <small>{brief.source === 'model' ? 'Checked against the ledger' : 'Basic summary (AI unavailable)'}</small></p>
          : <p className="muted">{briefError || `${persona} is reading this account…`}</p>}
      </div>
    </div>
  </section>;
}
